import { useState, useMemo, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import { categories } from '@/mocks/home';
import { categoryProducts, sortOptions } from '@/mocks/category';

const categoryNameMap: Record<string, string> = {
  dresses: '连衣裙',
  tops: '上衣',
  pants: '裤装',
  skirts: '半身裙',
  outerwear: '外套',
  accessories: '配饰',
  shoes: '鞋履',
  beauty: '美妆',
};

export default function CategoryPage() {
  const { slug = 'dresses' } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState('default');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === slug);
  const products = categoryProducts[slug] || [];
  const categoryName = categoryNameMap[slug] || slug;

  const sortedProducts = useMemo(() => {
    const list = [...products];
    switch (sortBy) {
      case 'price-asc':
        return list.sort((a, b) => a.price - b.price);
      case 'price-desc':
        return list.sort((a, b) => b.price - a.price);
      case 'newest':
        return list.reverse();
      default:
        return list;
    }
  }, [products, sortBy]);

  return (
    <>
      <SEOHead
        title={`蘑菇网${categoryName} - 精选时尚${categoryName} | 蘑菇网官网`}
        description={`蘑菇网${categoryName}专区，精选海量潮流${categoryName}，涵盖法式复古、极简通勤、甜美约会等多种风格。品质保证，价格实惠，让每位女性轻松找到心仪的${categoryName}单品。`}
        keywords={`蘑菇网${categoryName},${categoryName}推荐,时尚${categoryName},${categoryName}穿搭,蘑菇网`}
        canonicalUrl={`https://www.moguwang.com/category/${slug}`}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <div className="pt-16">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '分类' },
              { label: categoryName },
            ]}
          />

          {/* Category Header */}
          <section className="w-full bg-background-100 py-8 md:py-12">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-3">
                {categoryName}
              </h1>
              <p className="text-sm text-foreground-500 max-w-xl">
                精选{products.length}款{categoryName}单品，品质保证，风格多元
              </p>
            </div>
          </section>

          {/* Category Tabs */}
          <section className="w-full border-b border-background-200/70 sticky top-16 bg-background-50/95 backdrop-blur-md z-40">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-hide">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                      slug === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Sort & Product Grid */}
          <section className="w-full py-8 md:py-12">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-6">
                <p className="text-sm text-foreground-500">共 {products.length} 件商品</p>
                <div className="flex items-center gap-2">
                  {sortOptions.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setSortBy(opt.value)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-colors ${
                        sortBy === opt.value
                          ? 'bg-secondary-500 text-background-50'
                          : 'text-foreground-600 hover:bg-background-200'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop>
                {sortedProducts.map((product) => (
                  <div key={product.id} className="group cursor-pointer">
                    <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-200 mb-3">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <span className="absolute top-2 left-2 px-2 py-0.5 rounded text-xs font-medium bg-accent-500 text-background-50">
                        {product.tag}
                      </span>
                    </div>
                    <h3 className="text-sm font-medium text-foreground-900 mb-1 line-clamp-2">{product.name}</h3>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-primary-600">¥{product.price}</span>
                      <span className="text-xs text-foreground-400 line-through">¥{product.originalPrice}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>

        <Footer />
      </div>
    </>
  );
}